'use client'

import { Session } from '@/lib/next-shim'
import { getEnv } from '@/lib/env'
import { Link } from '@/lib/i18n/settings'
import { useListPrioritizedMeetingLinksQuery } from '@/core/graphql/hooks'
import { useTranslations } from '@/lib/next-shim'

type ZoomDocNYLandingClientProps = {
  session?: Session
}

export default function ZoomDocNYLandingClient({ session }: ZoomDocNYLandingClientProps) {
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO', '')
  const t = useTranslations()

  const { data, loading } = useListPrioritizedMeetingLinksQuery()
  const meetingLinks = data?.prioritizedMeetingLinks

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          {brandLogo && <img src={brandLogo} alt={brandName} className="h-16 mx-auto mb-4" />}
          <h1 className="text-2xl font-bold text-gray-900">{brandName}</h1>
        </div>

        {/* Main Content */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('page.Landing.heroTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('page.Landing.heroSubtitle')}</p>

          {/* Action Buttons */}
          <div className="space-y-4">
            {session ? (
              <>
                <Link
                  href="/account/dashboard"
                  className="w-full bg-primary text-white py-3 px-6 rounded-md font-medium hover:bg-opacity-90 transition-colors inline-block text-center"
                >
                  {t('page.Landing.myAccount')}
                </Link>
                <Link
                  href="/meeting-links"
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-md font-medium hover:bg-gray-200 transition-colors inline-block text-center"
                >
                  {t('page.Landing.bookAppointment')}
                </Link>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="w-full bg-primary text-white py-3 px-6 rounded-md font-medium hover:bg-opacity-90 transition-colors inline-block text-center"
                >
                  {t('page.Landing.signIn')}
                </Link>
                <Link
                  href="/register"
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-md font-medium hover:bg-gray-200 transition-colors inline-block text-center"
                >
                  {t('page.Landing.createAccount')}
                </Link>
                <Link href="/report-lost-device" className="block text-sm text-gray-500 underline hover:text-gray-700 transition-colors text-center">
                  {t('page.Landing.reportLostDevice')}
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Meeting Links Section */}
        {meetingLinks && meetingLinks.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('page.Landing.meetingLinksTitle')}</h3>
            <div className="space-y-3">
              {loading ? (
                <div className="space-y-3">
                  <div className="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
                  <div className="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
                </div>
              ) : (
                meetingLinks.map((meetingLink) => (
                  <Link
                    key={meetingLink._id}
                    href={`/meeting-links/${meetingLink.slug}`}
                    className="block p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{meetingLink.name}</h4>
                        {meetingLink.description && <p className="text-sm text-gray-600 mt-1">{meetingLink.description}</p>}
                        {meetingLink.specialities && meetingLink.specialities.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {meetingLink.specialities.slice(0, 2).map((speciality) => (
                              <span
                                key={speciality.code}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full"
                              >
                                <svg className="h-1 w-1 fill-primary" viewBox="0 0 6 6" aria-hidden="true">
                                  <circle cx={3} cy={3} r={3} />
                                </svg>
                                {speciality.name}
                              </span>
                            ))}
                            {meetingLink.specialities.length > 2 && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">
                                {t('page.Landing.moreCount', { count: meetingLink.specialities.length - 2 })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-primary">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-8">
          <p>{t('page.Landing.copyright', { year: new Date().getFullYear(), brand: brandName })}</p>
        </div>
      </div>
    </main>
  )
}
